import React, { useEffect, useRef, useState } from 'react';
import {
    BoldItalicUnderlineToggles,
    CreateLink,
    headingsPlugin,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    ListsToggle,
    maxLengthPlugin,
    MDXEditor,
    MDXEditorMethods,
    toolbarPlugin,
    UndoRedo
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { cn } from '@core/utils/cn';
import { FieldError, FieldLabel } from '../textfield';

export interface TextAreaProps {
    label: string;
    value?: string;
    height?: number;
    required?: boolean;
    error?: string;
    placeholder?: string;
    dataTestId?: string;
    characterLimit?: number;
    disabled?: boolean;
    onChange(value: string): void;
}

export const TextArea = ({
    label,
    value,
    height,
    required,
    error,
    placeholder,
    dataTestId,
    disabled,
    characterLimit,
    onChange
}: TextAreaProps) => {
    const [isFocused, setIsFocused] = useState(false);
    const ref = useRef<MDXEditorMethods>(null);
    const lastValueRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!ref.current || value === undefined) return;
        const nextValue = value || '';
        if (lastValueRef.current === nextValue) return;
        ref.current.setMarkdown(nextValue);
        lastValueRef.current = nextValue;
    }, [value]);

    return (
        <div
            data-testid={dataTestId}
            onClick={() => setIsFocused(true)}
            style={
                { '--mdx-min-h': `${height ?? 200}px` } as React.CSSProperties
            }
            className={cn(
                'flex flex-col gap-2 rounded-lg border p-4 transition-colors',
                isFocused && 'border-primary',
                error && 'border-destructive',
                '[&_.mdxeditor]:font-sans [&_.mdxeditor-toolbar]:rounded-md [&_[contenteditable]]:min-h-[var(--mdx-min-h)] [&_[contenteditable]]:p-0'
            )}
        >
            <FieldLabel label={label} required={required} />
            <MDXEditor
                className="dark-theme dark-editor"
                ref={ref}
                markdown={value || ''}
                onChange={(nextValue) => {
                    lastValueRef.current = nextValue;
                    onChange(nextValue);
                }}
                onBlur={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsFocused(false);
                }}
                readOnly={disabled}
                placeholder={placeholder}
                plugins={[
                    headingsPlugin(),
                    maxLengthPlugin(characterLimit),
                    linkPlugin(),
                    listsPlugin(),
                    linkDialogPlugin(),
                    toolbarPlugin({
                        toolbarContents: () => (
                            <>
                                <BoldItalicUnderlineToggles
                                    options={['Bold', 'Italic']}
                                />
                                <ListsToggle options={['bullet', 'number']} />
                                <CreateLink />
                                <UndoRedo />
                            </>
                        )
                    })
                ]}
            />
            <FieldError error={error} />
        </div>
    );
};

export default TextArea;
